import { useTranslation } from 'react-i18next';
import {
    AdaptiveCarousel,
    AdaptiveCarouselSlide,
    BasicResponsiveImage,
    BoundedContent,
    Callout,
    H2,
    Link,
    P,
    StandardTile,
    StandardTileContents,
    StandardTileImage,
    StandardTileName,
    StandardTileOverlay,
} from '@vp/swan';
import { sanitiseLocale } from '../../i18n/config';
import { useTrackNavigationClicked, useTrackPromotionViewed } from '../../utils/analytics';
import { getLaunchMyBusinessToolsConfig } from './LaunchMyBusinessToolsConfig';
import type { BusinessToolKey } from './LaunchMyBusinessToolsConfig';

interface BusinessToolTileProps {
    id: string;
    description: string;
    url: string;
    imageSrc: string;
    altText: string;
    trackingCategory: string;
    position: number;
}

const BusinessToolTile = ({
    id,
    description,
    url,
    imageSrc,
    altText,
    trackingCategory,
    position,
}: BusinessToolTileProps) => {
    const { t } = useTranslation();
    const trackingRef = useTrackNavigationClicked(
        trackingCategory,
        'Launch My Business Tools',
        url,
        id,
    );
    const promotionRef = useTrackPromotionViewed(
        trackingCategory,
        'Launch My Business Tools',
        url,
        id,
        'standard_tile_image',
        position,
    );

    return (
        <StandardTile
            skin="product"
            imageWidth="full-bleed"
            render={(props) => (
                <Link
                    className={props.className}
                    href={url}
                    ref={trackingRef}
                    skin="unstyled"
                    style={{ height: '100%' }} // Align bottom of Tiles
                >
                    {props.children}
                </Link>
            )}
        >
            {id === 'businessNameGenerator' && (
                <StandardTileOverlay>
                    <Callout skin="accent">
                        {t('Vista Logos > Launch My Business Tools > Callout > New.value')}
                    </Callout>
                </StandardTileOverlay>
            )}
            <StandardTileImage ref={promotionRef}>
                <BasicResponsiveImage aspectRatio={1} src={imageSrc} alt={altText} />
            </StandardTileImage>
            <StandardTileContents>
                <StandardTileName as="p">{description}</StandardTileName>
            </StandardTileContents>
        </StandardTile>
    );
};

interface LaunchMyBusinessToolsProps {
    header: string;
    subheader: string;
    businessTools: BusinessToolKey[];
    trackingCategory: string;
}

const LaunchMyBusinessTools = ({
    header,
    subheader,
    businessTools,
    trackingCategory,
}: LaunchMyBusinessToolsProps) => {
    const { t, i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);

    const launchMyBusinessTools = getLaunchMyBusinessToolsConfig(businessTools, t, locale);

    return (
        <BoundedContent marginY="between-sections" as="section" id="businessTools">
            <H2 fontSkin="title-section" id="business-tools-heading">
                {header}
            </H2>
            <P>{subheader}</P>
            <AdaptiveCarousel
                slidesToShow={{ xs: 2, md: 4, lg: 5, xl: 5 }}
                aria-labelledby="business-tools-heading"
                marginTop="between-subsections"
            >
                {launchMyBusinessTools.map(
                    ({ businessToolKey, description, url, image }, index) => (
                        <AdaptiveCarouselSlide key={businessToolKey} paddingY={2}>
                            <BusinessToolTile
                                id={businessToolKey}
                                description={description}
                                url={url}
                                imageSrc={image.src}
                                altText={image.altText}
                                trackingCategory={trackingCategory}
                                position={index + 1}
                            />
                        </AdaptiveCarouselSlide>
                    ),
                )}
            </AdaptiveCarousel>
        </BoundedContent>
    );
};

export default LaunchMyBusinessTools;
